import { AxiosInstance, AxiosResponse, AxiosError } from "axios";

/**
 * 端末制限：契約切替時の再ゲート
 *
 * 職員/事業所の切替(/api/auth/token = ChangeStaff)で、切替先契約が制限モード(1/2)の場合、
 * サーバは JWT を返さず need_device_gate + ゲートトークン(security_code) を返す。
 * その場合、切替先契約で許可された資格情報による WebAuthn アサーションを実行し、
 * 成功したら切替後 JWT を適用する。
 *
 * 同一端末(=同一Apple ID等)のパスキーしか提示できないため、切替先契約用の資格情報を
 * 持たない端末からは切替できず（onDenied）、端末分離が強制される。
 */

interface SwitchGateParams {
  /** auth backend のベースURL（window.auth_backend_url） */
  authBackendUrl: string;
  /** axios インスタンス（window.axios） */
  axios: AxiosInstance;
  /** 現在の JWT（/api/auth/token 呼び出し用） */
  jwt: string;
  /** 切替先の office_staff_id */
  officeStaffId: number;
  /** 切替成功（JWT取得）時 */
  onSuccess: (token: string, refToken: string) => void;
  /** 切替不可（未許可 / この端末に対象契約のパスキー無し）時 */
  onDenied: (message: string) => void;
  /** 予期せぬエラー時 */
  openAlert: (message: string) => void;
  /** ローディング表示切替（任意） */
  setLoading?: (loading: boolean) => void;
}

interface AssertionOption {
  allowCredentials: {
    id: string;
    transports?: AuthenticatorTransport[];
    type: PublicKeyCredentialType;
  }[];
  challenge: string;
  rpId: string;
  userVerification: UserVerificationRequirement;
  timeout: number;
}

function base64ToArrayBuffer(base64String: string): Uint8Array {
  const replaced = base64String.replace(/_/g, "/").replace(/-/g, "+");
  const decoded = window.atob(replaced);
  return Uint8Array.from(decoded, c => c.charCodeAt(0));
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const binary = String.fromCharCode(...new Uint8Array(buffer));
  return window
    .btoa(binary)
    .replace(/\//g, "_")
    .replace(/\+/g, "-")
    .replace(/=+$/g, "");
}

/** 切替先契約での WebAuthn アサーション → 切替後JWT適用 */
async function runGateAssertion(
  params: SwitchGateParams,
  gateToken: string,
  ewellUserId: number
): Promise<void> {
  // アサーション開始
  let option: AssertionOption;
  try {
    const beginRes: AxiosResponse = await params.axios.post(
      params.authBackendUrl + "/webauthn/assertion/begin",
      { ewell_user_id: ewellUserId }
    );
    option = beginRes.data.webauthn_response;
  } catch (error) {
    const e = error as AxiosError;
    // パスキー未登録（この端末に切替先契約の資格情報が無い）
    if (e.response && e.response.data && e.response.data.code == 101) {
      params.onDenied(
        "この端末には切替先の法人で許可された資格情報（パスキー）がありません。対象法人用の端末でログインしてください。"
      );
      return;
    }
    params.openAlert("サーバでエラーが発生しました。\n(" + error + ")");
    return;
  }

  // 生体認証（navigator.credentials.get）
  let credential: PublicKeyCredential | null;
  try {
    credential = (await navigator.credentials.get({
      publicKey: {
        allowCredentials: option.allowCredentials.map(cr => ({
          id: base64ToArrayBuffer(cr.id),
          transports: cr.transports,
          type: cr.type
        })),
        challenge: base64ToArrayBuffer(option.challenge),
        rpId: option.rpId,
        timeout: option.timeout,
        userVerification: option.userVerification
      }
    })) as PublicKeyCredential | null;
  } catch (e) {
    // ユーザーキャンセル等：切替を中断（エラー表示しない）
    if ((e as Error).name === "NotAllowedError") {
      return;
    }
    params.openAlert("認証に失敗しました。");
    return;
  }
  if (credential === null) {
    params.openAlert("認証に失敗しました。");
    return;
  }

  // アサーション完了 → 切替後JWT
  const assertionResponse = credential.response as AuthenticatorAssertionResponse;
  let userHandle = "";
  if (assertionResponse.userHandle) {
    userHandle = arrayBufferToBase64(assertionResponse.userHandle);
  }
  const assertion = {
    id: credential.id,
    rawId: arrayBufferToBase64(credential.rawId),
    type: credential.type,
    response: {
      authenticatorData: arrayBufferToBase64(
        assertionResponse.authenticatorData
      ),
      clientDataJSON: arrayBufferToBase64(assertionResponse.clientDataJSON),
      signature: arrayBufferToBase64(assertionResponse.signature),
      userHandle
    }
  };

  try {
    const compRes: AxiosResponse = await params.axios.post(
      params.authBackendUrl + "/webauthn/assertion/complete",
      { assertion, ewell_user_id: ewellUserId, token: gateToken }
    );
    if (compRes.data.token && compRes.data.ref_token) {
      params.onSuccess(compRes.data.token, compRes.data.ref_token);
      return;
    }
    if (
      compRes.data.device_access_denied ||
      compRes.data.device_limit_exceeded
    ) {
      params.onDenied(
        compRes.data.message ||
          "この端末では切替先の法人にアクセスできません。"
      );
      return;
    }
    params.openAlert("切替に失敗しました。");
  } catch (error) {
    params.openAlert("サーバでエラーが発生しました。\n(" + error + ")");
  }
}

/** 職員/事業所切替を実行し、必要なら端末ゲート（WebAuthn）を通す */
export async function switchStaffWithDeviceGate(
  params: SwitchGateParams
): Promise<void> {
  const setLoading = params.setLoading || (() => undefined);
  setLoading(true);
  try {
    const res: AxiosResponse = await params.axios.post(
      params.authBackendUrl + "/api/auth/token",
      { office_staff_id: params.officeStaffId },
      { headers: { Authorization: "Bearer " + params.jwt } }
    );

    // 制限なし：そのまま切替
    if (res.data.token && res.data.ref_token) {
      params.onSuccess(res.data.token, res.data.ref_token);
      return;
    }

    // 端末ゲートが必要（切替先が制限モード）
    if (res.data.need_device_gate) {
      await runGateAssertion(
        params,
        res.data.security_code,
        res.data.ewell_user_id
      );
      return;
    }

    params.openAlert("切替に失敗しました。");
  } catch (error) {
    params.openAlert("サーバでエラーが発生しました。\n(" + error + ")");
  } finally {
    setLoading(false);
  }
}
